import type { CSSProperties, ReactNode } from 'react';
import { formatHms, formatTotalSecondsCompact, secondsToHms } from '../lib/format';

/**
 * Static renders of real product screens for the marketing homepage. Built from the
 * app's own component classes (.card, .hms, .guesses, .badge, .btn) instead of
 * screenshots, so they stay crisp at any DPI and pick up UI changes for free. Each
 * mockup is a single role="img" with a description; nothing inside is focusable.
 *
 * /how-it-works is server-rendered markdown and hand-writes the same markup
 * (Content/pages/how-it-works.md) — keep the two in step if a mockup's structure changes.
 */

// Mirrors the resolved demo sweepstake at guessmytime.com/s/VR3RVTBE.
export const EXAMPLE = {
  path: '/s/VR3RVTBE',
  title: "Mark's Cardiff Half Marathon",
  organiser: 'Mark Rawson',
  charity: 'Tŷ Hafan',
  eventStarts: 'Sun 4 Oct, 09:00 BST',
  expectedSeconds: 6720,
  actualSeconds: 6860,
  // Sorted lowest first, as the public leaderboard shows them.
  guesses: [
    { name: 'Sioned', seconds: 6510 },
    { name: 'Bethan', seconds: 6765 },
    { name: 'Rhys', seconds: 6912 },
    { name: 'Catrin', seconds: 7170 },
    { name: 'Dai', seconds: 7500 },
  ],
};

const offBy = (seconds: number) => Math.abs(seconds - EXAMPLE.actualSeconds);

export const exampleWinner = EXAMPLE.guesses.reduce((best, g) =>
  offBy(g.seconds) < offBy(best.seconds) ? g : best,
);
export const exampleWinnerOffBy = offBy(exampleWinner.seconds);

// Seeded noise around three finder squares. Deliberately not a scannable code.
const QR_PATH =
  'M0 0h7v1H0zM8 0h1v1H8zM10 0h1v1H10zM14 0h3v1H14zM18 0h7v1H18zM0 1h1v1H0zM6 1h1v1H6zM8 1h6v1H8zM16 1h1v1H16zM18 1h1v1H18zM24 1h1v1H24zM0 2h1v1H0zM2 2h3v1H2zM6 2h1v1H6zM8 2h4v1H8zM14 2h3v1H14zM18 2h1v1H18zM20 2h3v1H20zM24 2h1v1H24zM0 3h1v1H0zM2 3h3v1H2zM6 3h1v1H6zM10 3h5v1H10zM18 3h1v1H18zM20 3h3v1H20zM24 3h1v1H24zM0 4h1v1H0zM2 4h3v1H2zM6 4h1v1H6zM8 4h2v1H8zM12 4h1v1H12zM18 4h1v1H18zM20 4h3v1H20zM24 4h1v1H24zM0 5h1v1H0zM6 5h1v1H6zM8 5h1v1H8zM16 5h1v1H16zM18 5h1v1H18zM24 5h1v1H24zM0 6h7v1H0zM9 6h1v1H9zM13 6h3v1H13zM18 6h7v1H18zM8 7h2v1H8zM12 7h1v1H12zM15 7h2v1H15zM1 8h1v1H1zM4 8h2v1H4zM7 8h4v1H7zM15 8h1v1H15zM17 8h1v1H17zM19 8h1v1H19zM22 8h1v1H22zM24 8h1v1H24zM2 9h2v1H2zM5 9h4v1H5zM10 9h3v1H10zM18 9h2v1H18zM21 9h1v1H21zM23 9h1v1H23zM4 10h1v1H4zM9 10h1v1H9zM12 10h2v1H12zM16 10h1v1H16zM19 10h4v1H19zM1 11h1v1H1zM4 11h1v1H4zM6 11h1v1H6zM9 11h1v1H9zM12 11h2v1H12zM0 12h1v1H0zM2 12h2v1H2zM5 12h2v1H5zM13 12h3v1H13zM17 12h1v1H17zM19 12h2v1H19zM23 12h2v1H23zM2 13h1v1H2zM4 13h2v1H4zM9 13h1v1H9zM11 13h1v1H11zM13 13h2v1H13zM16 13h2v1H16zM21 13h1v1H21zM23 13h2v1H23zM0 14h1v1H0zM5 14h1v1H5zM7 14h1v1H7zM10 14h5v1H10zM17 14h3v1H17zM23 14h2v1H23zM0 15h2v1H0zM3 15h1v1H3zM6 15h3v1H6zM10 15h2v1H10zM13 15h1v1H13zM16 15h1v1H16zM18 15h2v1H18zM21 15h1v1H21zM0 16h2v1H0zM8 16h1v1H8zM11 16h5v1H11zM20 16h1v1H20zM22 16h3v1H22zM8 17h1v1H8zM11 17h5v1H11zM23 17h1v1H23zM0 18h7v1H0zM9 18h1v1H9zM17 18h3v1H17zM21 18h3v1H21zM0 19h1v1H0zM6 19h1v1H6zM8 19h2v1H8zM12 19h1v1H12zM16 19h3v1H16zM24 19h1v1H24zM0 20h1v1H0zM2 20h3v1H2zM6 20h1v1H6zM9 20h2v1H9zM12 20h3v1H12zM16 20h2v1H16zM19 20h2v1H19zM22 20h1v1H22zM0 21h1v1H0zM2 21h3v1H2zM6 21h1v1H6zM8 21h2v1H8zM11 21h4v1H11zM16 21h3v1H16zM20 21h1v1H20zM24 21h1v1H24zM0 22h1v1H0zM2 22h3v1H2zM6 22h1v1H6zM8 22h2v1H8zM13 22h2v1H13zM20 22h1v1H20zM22 22h3v1H22zM0 23h1v1H0zM6 23h1v1H6zM9 23h1v1H9zM11 23h1v1H11zM16 23h2v1H16zM19 23h1v1H19zM22 23h2v1H22zM0 24h7v1H0zM9 24h3v1H9zM13 24h1v1H13zM15 24h1v1H15zM19 24h1v1H19zM24 24h1v1H24z';

function QrMark() {
  return (
    <svg className="qr" viewBox="0 0 25 25" width={220} height={220} shapeRendering="crispEdges" aria-hidden="true">
      <path d={QR_PATH} fill="currentColor" />
    </svg>
  );
}

function Mockup({ label, className, children }: { label: string; className: string; children: ReactNode }) {
  return (
    <div className={`mk ${className}`} role="img" aria-label={label}>
      {children}
    </div>
  );
}

function UrlPill({ path }: { path: string }) {
  return (
    <span className="mk-url">
      guessmytime.com<strong>{path}</strong>
    </span>
  );
}

function MockHms({ seconds, label, readbackPrefix }: { seconds: number; label: string; readbackPrefix: string }) {
  const hms = secondsToHms(seconds);
  return (
    <div className="field">
      <span className="mk-label">{label}</span>
      <div className="hms">
        {(['hours', 'minutes', 'seconds'] as const).map((unit) => (
          <div className="hms-cell" key={unit}>
            <span className="mk-input">{hms[unit]}</span>
            <span className="mk-label">{unit}</span>
          </div>
        ))}
      </div>
      <p className="hms-readback">
        {readbackPrefix}: <strong>{formatHms(hms.hours, hms.minutes, hms.seconds)}</strong>
      </p>
    </div>
  );
}

export function HeroPhoneMockup() {
  const guesser = EXAMPLE.guesses[0];
  return (
    <Mockup
      className="mk-phone"
      label={`A phone showing the guess page for ${EXAMPLE.title}. ${guesser.name} is entering a guess of ${formatTotalSecondsCompact(guesser.seconds)} in three big hours, minutes and seconds boxes.`}
    >
      <div className="mk-phone-screen">
        <div className="mk-bar">
          <UrlPill path={EXAMPLE.path} />
        </div>
        <div className="mk-page">
          <p className="trust">
            Organised by <strong>{EXAMPLE.organiser}</strong> in aid of{' '}
            <span className="trust-link"><strong>{EXAMPLE.charity}</strong></span>
          </p>
          <p className="mk-h1">{EXAMPLE.title}</p>
          <p className="hero-meta">
            <span className="badge badge-open">Open</span>
            <span className="hero-meta-when">
              Event starts <time>{EXAMPLE.eventStarts}</time>
            </span>
          </p>
          <div className="card">
            <p className="mk-h2">Make your guess</p>
            <div className="field">
              <span className="mk-label">Your name</span>
              <span className="mk-input">{guesser.name}</span>
            </div>
            <MockHms seconds={guesser.seconds} label="Finish time guess" readbackPrefix="Your guess" />
            <span className="btn btn-primary btn-block">Submit guess</span>
          </div>
        </div>
      </div>
    </Mockup>
  );
}

export function CreateTileMockup() {
  return (
    <Mockup
      className="mk-tile"
      label="The expected finish time on the create form, entered as 1 hour, 52 minutes and 0 seconds in three big boxes."
    >
      <div className="mk-tile-page card">
        <MockHms seconds={EXAMPLE.expectedSeconds} label="Expected finish time (optional)" readbackPrefix="Expected finish" />
      </div>
    </Mockup>
  );
}

export function ShareTileMockup() {
  return (
    <Mockup
      className="mk-tile"
      label="The share kit: a short guessmytime.com link, Copy link and WhatsApp buttons, and a QR code."
    >
      <div className="mk-tile-page card mk-share-row">
        <QrMark />
        <div className="mk-share-actions">
          <UrlPill path={EXAMPLE.path} />
          <span className="btn">Copy link</span>
          <span className="btn">WhatsApp</span>
        </div>
      </div>
    </Mockup>
  );
}

export function RevealTileMockup() {
  return (
    <Mockup
      className="mk-tile"
      label={`The final guesses, sorted lowest first, with ${exampleWinner.name} marked as the winner.`}
    >
      <div className="mk-tile-page card">
        <table className="guesses">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col" className="num">Guess</th>
            </tr>
          </thead>
          <tbody>
            {EXAMPLE.guesses.map((g) => {
              const isWinner = g === exampleWinner;
              return (
                <tr key={g.name} className={isWinner ? 'is-winner' : undefined}>
                  <td>
                    {g.name}
                    {isWinner && <> 🏆</>}
                  </td>
                  <td className="num">{formatTotalSecondsCompact(g.seconds)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Mockup>
  );
}

/** Every guess plotted on one line against the actual finish time. */
export function GuessTimeline() {
  const all = [...EXAMPLE.guesses.map((g) => g.seconds), EXAMPLE.actualSeconds];
  const lo = Math.min(...all) - 150;
  const hi = Math.max(...all) + 120;
  const at = (seconds: number) => ({ '--at': `${((seconds - lo) / (hi - lo)) * 100}%` }) as CSSProperties;
  const actual = formatTotalSecondsCompact(EXAMPLE.actualSeconds);

  return (
    <div
      className="gtl"
      role="img"
      aria-label={`All ${EXAMPLE.guesses.length} guesses plotted against the actual finish time of ${actual}. ${exampleWinner.name}'s guess of ${formatTotalSecondsCompact(exampleWinner.seconds)} is the closest.`}
    >
      <div className="gtl-track">
        <span className="gtl-finish" style={at(EXAMPLE.actualSeconds)}>
          <span className="gtl-label">
            Actual finish<b>{actual}</b>
          </span>
        </span>
        {EXAMPLE.guesses.map((g, i) => (
          <span
            key={g.name}
            className={['gtl-guess', i % 2 === 1 && 'is-low', g === exampleWinner && 'is-winner'].filter(Boolean).join(' ')}
            style={at(g.seconds)}
          >
            <span className="gtl-dot" />
            <span className="gtl-label">
              {g.name}
              <b>{formatTotalSecondsCompact(g.seconds)}</b>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
